import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { usePanelParams } from '../../usePanelParams';
import { DataObjectValidFormDataType } from '../../schema';
import { AlgorithmForm } from '../AlgorithmForm';
import { AlgorithmTargetsFormDataType } from '../targets/schema/types';
import {
  AlgorithmFilter,
  useListAlgorithmsQuery,
} from '../query/useGetAlgorithmsQuery';
import { useDeleteAlgorithmsByIdsMutation } from '../query/useDeleteAlgorithmsByIdsMutation';
import {
  AlgorithmsTableView,
  AlgorithmsTableViewRef,
} from './AlgoritmsTableView';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutateAlgorithmQuery } from '../query/useMutateAlgorithmQuery';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../AlgorithmForm.translations.json';
import validationTranslations from '../../translations/validation.translations.json';
import { AlgorithmCloneModal } from '../clone/AlgorithmCloneModal';
import { AlgorithmCloneProps, CloneModalState } from '../utils';
import { ApiError } from '../../../common/ApiClient';
import { TestCalculateAlgorithmsModal } from '../testRun/AlgorithmTestCalculateModal';
import { AlgorithmExtendedType } from '../schema/types';
import { DataObjectType } from '../../types';
import { useBrokenReferences } from '../brokenReferences/useBrokenReferences';
import { useAlertContext } from '../../../common/form';
import { BrokenReferencesAlertWithDrawer } from '../brokenReferences/BrokenReferencesAlertWithDrawer';
import { useDataObjectFormLabelTranslation } from '../../useDataObjectFormLabelTranslation';
import styled from 'styled-components';

const { addTranslations, t } = i18nNsFactory('algorithm-form-table');
addTranslations(translations);
addTranslations(validationTranslations);

export interface AlgorithmsFormTableProps {
  isClone?: boolean;
  onAlgorithmsCountChange?: (count: number) => void;
  setMutationErrors: (
    apiErrors: ApiError[],
    getErrorMessage?: GetApiErrorMessage,
  ) => void;
  clearMutationErrors: any;
}

export const AlgorithmsFormTable: FC<AlgorithmsFormTableProps> = (props) => {
  const {
    isClone,
    onAlgorithmsCountChange,
    setMutationErrors,
    clearMutationErrors,
  } = props;

  const { t: tLabel } = useDataObjectFormLabelTranslation();
  const [isAlgorithmView, setIsAlgorithmView] = useState(true);
  const [filter, setFilter] = useState<AlgorithmFilter>({});
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const tableViewRef = useRef<AlgorithmsTableViewRef>(null);
  const [testRunAlgorithms, setTestRunAlgorithms] = useState<
    AlgorithmExtendedType[] | null
  >(null);
  const [algorithmClone, setAlgorithmClone] =
    useState<AlgorithmCloneProps | null>(null);
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const {
    data: algorithms = [],
    isFetching,
    refetch,
  } = useListAlgorithmsQuery(
    {
      dataObjectVersionId: dataObjectDescriptionVersion.versionId,
      isAlgorithmView,
      ...filter,
    },
    {
      enabled: !isClone,
      initialData: [],
    },
  );

  useEffect(() => {
    if (algorithms && onAlgorithmsCountChange) {
      onAlgorithmsCountChange(algorithms.length);
    }
  }, [algorithms, onAlgorithmsCountChange]);

  const { mutateAsync } = useMutateAlgorithmQuery();
  const multipleMutation = useDeleteAlgorithmsByIdsMutation();
  const [{ algorithmId }, setPanelParams] = usePanelParams();

  const { resetAlert, setSuccess } = useAlertContext();
  const handleCreate = useCallback(
    (algorithmId: number) => {
      setPanelParams(
        { algorithmId },
        {
          state: { saved: 'success', view: 'algorithm' },
        },
      );
      setAlgorithmClone(null);
    },
    [setPanelParams, setAlgorithmClone],
  );

  const handleCloseEditPanel = useCallback(() => {
    void refetch();
    setPanelParams();
  }, [refetch, setPanelParams]);

  const setRequiredKeysRelatedAlgorithmIds =
    useSetRequiredKeysRelatedAlgorithmIds();

  const handleDeleteRow = useCallback(
    (algorithmId: number) => {
      clearMutationErrors();
      return mutateAsync({ id: algorithmId })
        .then(() => {
          setRequiredKeysRelatedAlgorithmIds({
            algorithmId,
            targets: algorithms.find(({ id }) => id === algorithmId)?.targets,
          });
          handleCloseEditPanel();
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      clearMutationErrors,
      mutateAsync,
      setRequiredKeysRelatedAlgorithmIds,
      algorithms,
      handleCloseEditPanel,
      setMutationErrors,
    ],
  );

  const handleDeleteSelectedRows = useCallback(
    (algorithmIds: number[]) => {
      if (!Array.isArray(algorithmIds) || algorithmIds.length === 0) {
        return undefined;
      }
      clearMutationErrors();

      multipleMutation.mutate(algorithmIds, {
        onSuccess: () => {
          algorithmIds.forEach((a) => {
            setRequiredKeysRelatedAlgorithmIds({
              algorithmId: a,
              targets: algorithms.find(({ id }) => id === a)?.targets,
            });
          });
          handleCloseEditPanel();
          setSelected(new Set());
        },
        onError: (error) => {
          setMutationErrors((error as any)?.response?.data?.errors ?? []);
        },
      });
    },
    [
      multipleMutation,
      clearMutationErrors,
      setRequiredKeysRelatedAlgorithmIds,
      algorithms,
      handleCloseEditPanel,
      setMutationErrors,
    ],
  );

  const handleTestRunSelectedRows = (algorithms: AlgorithmExtendedType[]) => {
    setTestRunAlgorithms(algorithms);
  };

  const selectedAlgorithmIds = useMemo(
    () =>
      Array.from(selected)
        .map(
          (a) =>
            tableViewRef?.current?.getTable()?.getRowModel().rowsById[a]
              ?.original?.id,
        )
        .filter((id): id is number => id !== undefined),
    [selected],
  );

  const {
    brokenReferencesMap,
    refetch: refetchBrokenReferences,
    isFetching: isFetchingBrokenReferences,
  } = useBrokenReferences(selectedAlgorithmIds);

  const handleCheckBrokenReferencesSelectedRows = useCallback(async () => {
    resetAlert();
    await refetchBrokenReferences();
  }, [resetAlert, refetchBrokenReferences]);

  useEffect(() => {
    if (brokenReferencesMap?.size === 0) {
      setSuccess(t('alert.no-broken-references'));
    }
  }, [brokenReferencesMap?.size, setSuccess]);

  const handleCloneRow = useCallback(
    (algorithmId: number, state: CloneModalState) => {
      setAlgorithmClone({ algorithmId, state });
    },
    [setAlgorithmClone],
  );

  const closeCloneModal = useCallback(() => {
    setAlgorithmClone(null);
  }, [setAlgorithmClone]);

  // Do not show form for unknown algorithmId
  const showForm = !!algorithmId;

  return (
    <>
      <BrokenReferencesAlertFullWidth
        brokenReferencesMap={brokenReferencesMap}
        tLabel={tLabel}
      />

      <AlgorithmsTableView
        ref={tableViewRef}
        algorithms={algorithms}
        onCloneRow={handleCloneRow}
        onDeleteRow={handleDeleteRow}
        onDeleteSelectedRows={handleDeleteSelectedRows}
        onTestRunSelectedRows={
          dataObjectDescriptionVersion?.dataObjectType === DataObjectType.OUT
            ? handleTestRunSelectedRows
            : undefined
        }
        onCheckBrokenReferencesSelectedRows={
          handleCheckBrokenReferencesSelectedRows
        }
        isLoading={isFetching || isFetchingBrokenReferences}
        onChangeView={setIsAlgorithmView}
        filter={filter}
        onFilter={setFilter}
        selected={selected}
        setSelected={setSelected}
      />
      {showForm && (
        <AlgorithmForm
          onCreate={handleCreate}
          onClone={handleCloneRow}
          onClose={handleCloseEditPanel}
          onDelete={handleDeleteRow}
          onChangeTargets={setRequiredKeysRelatedAlgorithmIds}
        />
      )}
      {algorithmClone && (
        <AlgorithmCloneModal
          algorithmId={algorithmClone.algorithmId}
          isFinish={algorithmClone.state === CloneModalState.CLONE_FINISH}
          onCreate={handleCreate}
          onClose={closeCloneModal}
        />
      )}
      {testRunAlgorithms && (
        <TestCalculateAlgorithmsModal
          algorithms={testRunAlgorithms}
          onClose={() => setTestRunAlgorithms(null)}
        />
      )}
    </>
  );
};

const useSetRequiredKeysRelatedAlgorithmIds = () => {
  const { setValue, getValues } = useFormContext<DataObjectValidFormDataType>();

  return useCallback(
    ({ algorithmId, targets }: AlgorithmTargetsFormDataType) => {
      const usedKeys = targets?.map((target) => target.targetKey);

      getValues('requiredKeys').forEach(
        ({ key, relatedAlgorithmIds }, index) => {
          const isThisKeyUsedByUpdatedAlgorithm = usedKeys?.includes(key);
          const isUpdatedAlgorithmRelatedToThisKey =
            relatedAlgorithmIds.includes(algorithmId);

          if (
            isThisKeyUsedByUpdatedAlgorithm &&
            !isUpdatedAlgorithmRelatedToThisKey
          ) {
            setValue(
              `requiredKeys.${index}.relatedAlgorithmIds`,
              relatedAlgorithmIds.concat(algorithmId),
            );
          }

          if (
            !isThisKeyUsedByUpdatedAlgorithm &&
            isUpdatedAlgorithmRelatedToThisKey
          ) {
            setValue(
              `requiredKeys.${index}.relatedAlgorithmIds`,
              relatedAlgorithmIds.filter((id) => id !== algorithmId),
            );
          }
        },
      );
    },
    [getValues, setValue],
  );
};

const BrokenReferencesAlertFullWidth = styled(BrokenReferencesAlertWithDrawer)`
  width: 100%;
`;
